import { yupResolver } from '@hookform/resolvers/yup';
import { type FieldValues, type Resolver } from 'react-hook-form';

/**
 * A typed wrapper around `yupResolver` that bridges the type gap between
 * Yup's `InferType` and the component's `FormValues` generic.
 *
 * In `@hookform/resolvers` v5+, `yupResolver` returns
 * `Resolver<SchemaInput, Context, Yup.InferType<schema>>` where the 3rd
 * generic (`TTransformedValues`) often differs from the `useForm<T>()` type.
 * This wrapper casts the result to `Resolver<TFieldValues>` so the resolver,
 * `Control`, and `SubmitHandler` types all align with the form's generic.
 *
 * @deprecated Use the native `yupResolver` from `@hookform/resolvers/yup` instead.
 * Derive your form type from the schema: `type FormValues = yup.InferType<typeof schema>`
 * so that `InferType` and `useForm<T>` agree, eliminating the need for this cast wrapper.
 *
 * @example
 * ```ts
 * // Preferred (no wrapper):
 * const schema = yup.object({ name: yup.string().required() });
 * type FormValues = yup.InferType<typeof schema>;
 * const { handleSubmit, control } = useForm<FormValues>({
 *   resolver: yupResolver(schema),
 * });
 *
 * // Legacy (this wrapper):
 * const { handleSubmit, control } = useForm<FormValues>({
 *   resolver: typedYupResolver<FormValues>(schema),
 * });
 * ```
 */
export const typedYupResolver = <TFieldValues extends FieldValues>(
  schema: Parameters<typeof yupResolver>[0],
  schemaOptions?: Parameters<typeof yupResolver>[1],
  resolverOptions?: Parameters<typeof yupResolver>[2],
): Resolver<TFieldValues> =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (yupResolver as (...args: any[]) => any)(schema, schemaOptions, resolverOptions) as unknown as Resolver<TFieldValues>;
